// frontend/src/components/admin/tenants/ouTree/SubOuCollapseGroup.tsx
/**
 * Grupo colapsavel de UMA Sub-OU (kind=environment) dentro do card de UO pai.
 *
 * Encapsula:
 *  - Header clicavel: chevron + env pill (PRD/HML/QA/DEV/...) + contagem
 *    "{N} conta(s)" ou "(sem contas)" quando vazia + ⋮ menu opcional.
 *  - Body (Collapse): N <EnvironmentRow hideEnvPill /> uma por conta da
 *    Sub-OU. A pill nao se repete nas rows — o anchor visual eh uma faixa
 *    colorida vertical fina (4px) na cor do env.
 *
 * Estado de expand:
 *  - Default: aberto se accounts.length>0, fechado se vazio.
 *  - matchesSearch=true forca aberto.
 *  - localStorage override aplicavel (mesmo Set que o OuBranchNode pai).
 */
import { useState, useCallback, useMemo } from 'react'
import type { KeyboardEvent } from 'react'
import { Box, Group, Text, Collapse, ActionIcon, Tooltip, Menu } from '@mantine/core'
import { IconChevronRight, IconDots, IconPlus } from '@tabler/icons-react'
import type { AwsOuNode, EnvName } from '@/api/awsOrgProvisioning'
import { ENV_OPTIONS } from '@/api/awsOrgProvisioning'
import { EnvironmentRow } from './EnvironmentRow'
import { colorForEnv } from './envOrdering'
import { AddEnvironmentModal } from '../modals/AddEnvironmentModal'
import type { TenantRow } from '../TenantsTable'

const ENV_SET = new Set<string>(ENV_OPTIONS)

interface RowProps {
  onToggleField: (id: number, field: string, value: boolean) => void
  onToggleZabbix: (tenantId: number, enabled: boolean) => void
  onToggleGitlab: (tenantId: number, currentlyEnabled: boolean) => void
  onToggleWhatsApi: (tenantId: number, enabled: boolean) => void
  onUpdateTimezone?: (tenantId: number, timezone: string | null) => void
  onEditRegions?: (tenant: TenantRow) => void
  onProvisioningFailedClick?: (tenant: TenantRow) => void
  fieldPendingIds?: Set<number>
  whatsapiPendingIds?: Set<number>
  timezonePendingIds?: Set<number>
}

interface Props {
  subOu: AwsOuNode
  accounts: TenantRow[]
  /**
   * matchesSearch herdado do ramo correspondente (do builder). Quando
   * true, forca o group aberto independente do estado salvo.
   */
  matchesSearch?: boolean
  expandedSet: Set<string>
  onToggleExpand: (ouId: string) => void
  /**
   * Tenant ID da UO pai (kind=tenant). Quando presente, exibe acao
   * "Adicionar conta neste ambiente" no menu (abre AddEnvironmentModal
   * pre-selecionando o env desta Sub-OU). Quando undefined, o menu eh
   * omitido.
   */
  parentTenantId?: number
  /**
   * Lista de envs ja existentes no parent (usada pra desabilitar opcoes
   * no AddEnvironmentModal). Quando parentTenantId undefined, ignorado.
   */
  parentExistingEnvs?: EnvName[]
  rowProps: RowProps
}

function getDefaultExpanded(
  ouId: string,
  hasAccounts: boolean,
  matchesSearch: boolean,
  expandedSet: Set<string>,
): boolean {
  if (matchesSearch) return true
  const defaultOpen = hasAccounts
  const isToggled = expandedSet.has(ouId)
  return isToggled ? !defaultOpen : defaultOpen
}

export function SubOuCollapseGroup({
  subOu,
  accounts,
  matchesSearch = false,
  expandedSet,
  onToggleExpand,
  parentTenantId,
  parentExistingEnvs,
  rowProps,
}: Props) {
  const [addEnvOpen, setAddEnvOpen] = useState(false)

  const ouId = String(subOu.ou_id)
  const envName = String(subOu.name).toUpperCase()
  const envColor = colorForEnv(subOu.name)
  const hasAccounts = accounts.length > 0
  const expanded = getDefaultExpanded(ouId, hasAccounts, matchesSearch, expandedSet)
  const showMenu = parentTenantId !== undefined

  const handleToggle = useCallback(() => {
    onToggleExpand(ouId)
  }, [onToggleExpand, ouId])

  const handleKey = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onToggleExpand(ouId)
      }
    },
    [onToggleExpand, ouId],
  )

  // Pre-select este env quando abrir o AddEnvironmentModal — so se o nome
  // for um env canonico (PRD/HML/QA/DEV/STG/SBOX/etc).
  const presetEnv: EnvName | undefined = ENV_SET.has(envName)
    ? (envName as EnvName)
    : undefined

  const countText = useMemo(() => {
    if (!hasAccounts) return '(sem contas)'
    return `${accounts.length} ${accounts.length === 1 ? 'conta' : 'contas'}`
  }, [hasAccounts, accounts.length])

  return (
    <>
      <Box
        data-testid={`sub-ou-group-${ouId}`}
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
        }}
      >
        <Box
          px="md"
          py="sm"
          onClick={handleToggle}
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Recolher' : 'Expandir'} ${subOu.name}`}
          onKeyDown={handleKey}
          data-testid={`sub-ou-chevron-${ouId}`}
          style={{
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'background 120ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--mantine-color-default-hover)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent'
          }}
        >
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" align="center" style={{ flex: 1, minWidth: 0 }}>
              <IconChevronRight
                size={14}
                style={{
                  transition: 'transform 200ms',
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  color: 'var(--mantine-color-dimmed)',
                  flexShrink: 0,
                }}
              />
              {/* env pill — mesma estetica de EnvironmentRow.envName */}
              <Box
                data-testid={`sub-ou-pill-${ouId}`}
                style={{
                  minWidth: 56,
                  padding: '3px 10px',
                  borderRadius: 4,
                  background: `var(--mantine-color-${envColor}-light)`,
                  color: `var(--mantine-color-${envColor}-7)`,
                  fontFamily: 'monospace',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                {envName}
              </Box>
              <Text
                size="xs"
                c="dimmed"
                fs={hasAccounts ? undefined : 'italic'}
                style={{ whiteSpace: 'nowrap' }}
              >
                {countText}
              </Text>
            </Group>

            {showMenu && (
              <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                <Menu shadow="md" width={240} position="bottom-end" withinPortal>
                  <Menu.Target>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      aria-label={`Ações do ambiente ${subOu.name}`}
                      data-testid={`sub-ou-actions-${ouId}`}
                    >
                      <IconDots size={14} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>{subOu.name}</Menu.Label>
                    <Tooltip
                      label={
                        presetEnv
                          ? `Provisionar conta em ${envName}`
                          : 'Sub-OU custom: abrir wizard genérico'
                      }
                      withArrow
                      position="left"
                    >
                      <Menu.Item
                        leftSection={<IconPlus size={14} />}
                        onClick={() => setAddEnvOpen(true)}
                      >
                        Adicionar conta neste ambiente
                      </Menu.Item>
                    </Tooltip>
                  </Menu.Dropdown>
                </Menu>
              </Group>
            )}
          </Group>
        </Box>

        <Collapse in={expanded}>
          {hasAccounts && (
            <Box
              style={{
                borderTop: '1px solid var(--mantine-color-default-border)',
              }}
            >
              {accounts.map((tenant, idx) => (
                <EnvironmentRow
                  key={`acc-${tenant.id}`}
                  envName={envName}
                  envColor={envColor}
                  tenant={tenant}
                  showSeparator={idx < accounts.length - 1}
                  hideEnvPill
                  {...rowProps}
                />
              ))}
            </Box>
          )}
        </Collapse>
      </Box>

      {parentTenantId !== undefined && addEnvOpen && (
        <AddEnvironmentModal
          opened={addEnvOpen}
          onClose={() => setAddEnvOpen(false)}
          tenantId={parentTenantId}
          currentEnvs={parentExistingEnvs ?? []}
          onSuccess={() => setAddEnvOpen(false)}
        />
      )}
    </>
  )
}
